import './header.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { Pages, Modals, OrderType } from '../../../types/enums';
import { Util } from '../../../utils/util';
import { LocalizationService } from '../../../services/localization-service';
import { Context } from '../../../models/context';
import { Router } from '../../../core/router';
import { Data } from '../../../models/data';
import { OrderLocationBar } from '../OrderLocationBar/order-location-bar';
import { OrderService } from '../../../services/order-service';
import { ProfileService } from '../../../services/profile-service';
import { LocationService } from '../../../services/location-service';
import { IAddressFormat } from '../../../interfaces/global.interfaces';

interface IHeaderModules {
    orderLocationBar: OrderLocationBar
}

export class Header {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _displayList: { [key: string]: any } = {};
    private readonly _orderService: OrderService;
    private readonly _localizationService: LocalizationService;
    private readonly _profileService: ProfileService;
    private readonly _locationService: LocationService;
    private _closestDeliveryStoreKey: string = "";
    private _isLandscape: boolean | undefined;
    private _isLoggedIn: boolean;
    private _brandNavigationItems: HTMLElement | null = null;
    private readonly _previousPaths: Pages[] = [Pages.LANDING];
    private readonly _modules: IHeaderModules;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, orderService: OrderService, profileService: ProfileService, locationService: LocationService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._orderService = orderService;
        this._localizationService = localizationService;
        this._profileService = profileService;
        this._locationService = locationService;
        this._isLoggedIn = false;

        this._brandNavigationItems = document.querySelector("#span_header_brand_items");

        this._createDisplayList();
        this._eventBusListeners();
        this._modules = this._initializeModules();
    }

    private readonly _createDisplayList = (): void => {
        this._displayList[Pages.LANDING] = { showHamburger: true, showBackArrow: false, showLogo: true, text: "", showRightSide: true, showLogin: true, showCart: true, showLocationBar: false };
        this._displayList[Pages.LOCATIONS] = { showHamburger: false, showBackArrow: true, showLogo: false, text: "SelectLocation", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.MENU] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "Menu", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.MENU_VIEW_ALL] = { showHamburger: false, showBackArrow: true, showLogo: false, text: "", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.REWARDS] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "MyRewards", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.FAVORITES] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "Favorites", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.ORDERS] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "RecentOrders", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.TRACK_ORDER] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "", showRightSide: true, showLogin: false, showCart: true, showLocationBar: false };
        this._displayList[Pages.TERMS] = { showHamburger: true, showBackArrow: false, showLogo: true, text: "", showRightSide: true, showLogin: true, showCart: true, showLocationBar: false };
        this._displayList[Pages.ACCOUNT] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "MyAccount", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.CART] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "REVIEW_ORDER", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
        this._displayList[Pages.CHECKOUT] = { showHamburger: true, showBackArrow: false, showLogo: false, text: "", showRightSide: true, showLogin: true, showCart: true, showLocationBar: true };
    }

    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.LOGGED_IN, async () => { await this._loggedInEvent(); });
        this._eventBus.on(EventTypes.LOGGED_OUT, this._loggedOutEvent);
        this._eventBus.on(EventTypes.LOCATION_CHANGE, this._locationChanged);
        this._eventBus.on(EventTypes.HEADER_NAME_CHANGE, this._headerNameChange);
        this._eventBus.on(EventTypes.CART_CHANGED, this._cartChanged);
        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, this._onBrandLoad);
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._orderTypeChanged);
    }

    private _initializeModules(): IHeaderModules {
        return {
            orderLocationBar: new OrderLocationBar(this._eventBus, this._data, this._context, this._localizationService, this._orderService, this._profileService, true)
        };
    }

    public init(): void {
        this._renderPage();
        this._setEventListeners();
    }

    private _renderPage(): void {
        this._setLocationBar();
        this._cartChanged();
        this._updateOrientation();
    }

    private _setLocationBar(): void {
        const locationBar = document.getElementById("div_header_location_bar");
        if (locationBar) {
            locationBar.innerHTML = "";
            locationBar.append(this._modules.orderLocationBar.element);
        }
    }

    private _setEventListeners(): void {
        const hamburgerEl = document.getElementById("div_header_hamburger_menu");

        hamburgerEl?.addEventListener("click", (e: Event) => {
            e.stopPropagation();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.HAMBURGER);
        });

        Util.addKeyboardActivation(hamburgerEl, () => this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.HAMBURGER));

        document.getElementById("btn_header_login")?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN)
        });

        Util.setElement("onclick", "div_header_arrow_back", this._handleBackNavigation);

        Util.setElement("onclick", "div_header_cancel", () => {
            this._eventBus.emit(EventTypes.HEADER_CANCEL);
        });

        Util.setElement("onclick", "div_header_store_logo", () => { this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING); });
        document.getElementById("div_header_profile")?.addEventListener("click", (e: Event) => {
            e.stopPropagation();
            if (this._isLandscape)
                this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.HEADER_ACCOUNT)
            else
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ACCOUNT);
        });
        document.getElementById("btn_header_shopping_bag")?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.CART);
        });

        addEventListener("resize", () => this._updateOrientation());

        document.querySelectorAll<HTMLElement>("#div_header_navigation [data-page]")
            ?.forEach(e => e.onclick = this._handleNavClick);
    }

    private readonly _handleNavClick = (event: Event): void => {
        event.stopImmediatePropagation();
        const element = (event.target as HTMLElement)?.closest<HTMLElement>("[data-page]");
        if (!element) return;

        const page = Pages[element.dataset.page as keyof typeof Pages];
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, page);
    }

    private readonly _loggedInEvent = async (): Promise<void> => {
        Util.hideElement("div_header_login_placeholder");
        Util.hideElement("btn_header_login");
        Util.showElement("div_header_profile");

        this._isLoggedIn = true;
        const brandId = this._data.getBrandProperty("StoreInfo").brandId;
        this._closestDeliveryStoreKey = await this._locationService.getClosestDeliveryStoreKeyByUserAsync(brandId, this._profileService.user);
        this._updateCartDisplay();
    }

    private readonly _loggedOutEvent = (): void => {
        Util.hideElement("div_header_login_placeholder");
        Util.showElement("btn_header_login");

        if (!this._isLandscape)
            Util.hideElement("div_header_profile");

        this._isLoggedIn = false;
        this._updateCartDisplay();
    }

    private readonly _locationChanged = (page: Pages): void => {
        const temp = this._displayList[page];
        if (!temp)
            return;

        this._previousPaths.push(Router.path);

        (temp.showHamburger) ? Util.showElement("div_header_hamburger_menu") : Util.hideElement("div_header_hamburger_menu");
        (temp.showBackArrow) ? Util.showElement("div_header_arrow_back") : Util.hideElement("div_header_arrow_back");
        (temp.showLogo) ? Util.showElement("div_header_store_logo") : Util.hideElement("div_header_store_logo");
        (temp.showRightSide) ? Util.showElement("div_header_right") : Util.hideElement("div_header_right");
        (temp.showLocationBar) ? Util.showElement("div_header_location_bar") : Util.hideElement("div_header_location_bar"); 
        (temp.showLogin) ? Util.showElement("div_header_login") : Util.hideElement("div_header_login");
        (temp.showCart) ? Util.showElement("div_header_shopping_bag") : Util.hideElement("div_header_shopping_bag");

        if (temp.text === null) {
            Util.showElement("div_header_center_text");
        } else {
            if (temp.text) {
                this._headerNameChange({ name: this._localizationService.names(temp.text), ltag: temp.text })
            } else {
                Util.hideElement("div_header_center_text");
                Util.setElement("innerText", "div_header_center_text", "");
            }
        }
        Util.hideElement("div_header_center_subtext");
        this._applyOrderHubMode();
    }

    private readonly _headerNameChange = (data: { name: string, ltag?: string, ltagj?: string }): void => {
        if (data.name === "")
            return;

        Util.showElement("div_header_center_text");
        Util.setElement("innerHTML", "div_header_center_text", data.name);

        if (data.ltag)
            Util.setElement("ltag", "div_header_center_text", data.ltag);

        if (data.ltagj)
            Util.setElement("ltagj", "div_header_center_text", data.ltagj);
    }

    private readonly _cartChanged = (): void => {
        this._updateCartDisplay();
    }

    private _updateCartDisplay(): void {
        const items = this._orderService.order.getAmountOfItemsInCart();
        const hasItems = items > 0;

        // Item badge
        if (hasItems) {
            Util.setElement("innerText", "spn_header_shopping_bag_items", items);
            Util.showElement("spn_header_shopping_bag_items");
        } else {
            Util.hideElement("spn_header_shopping_bag_items");
        }

        // Order total — always visible (includes tax, charges, discounts)
        const total: number = Number(this._orderService.order.getProperty("Total")) || 0;
        const country = this._data.getProperty("StoreInfo")?.country;
        const formattedTotal = country ? Util.formatMoney(total, country) : `$${total.toFixed(2)}`;

        const totalEl = document.getElementById("spn_header_cart_total");
        if (totalEl) {
            totalEl.textContent = formattedTotal;
            Util.showElement("spn_header_cart_total");
        }

        // Highlight cart button when logged in and cart has items
        const cartBtn = document.getElementById("btn_header_shopping_bag");
        if (cartBtn) {
            // Dynamic aria-label reflects live item count and total
            const itemLabel = items === 1 ? "1 item" : `${items} items`;
            cartBtn.setAttribute("aria-label", hasItems ? `Cart: ${itemLabel}, ${formattedTotal}` : `Cart: ${formattedTotal}`);
        }

        // Mobile: left-justify page name whenever cart has items
        const headerEl = document.querySelector<HTMLElement>(".header");
        if (headerEl) {
            if (hasItems && !this._isLandscape)
                headerEl.classList.add("guest-cart-active");
            else
                headerEl.classList.remove("guest-cart-active");
        }
    }

    private readonly _mobileElements = () => document.querySelectorAll<HTMLElement>(".header :is(.header-center, .header-left)");

    private readonly _desktopElements = () => document.querySelectorAll<HTMLElement>(".header #div_header_navigation");

    private static readonly MIN_DESKTOP_WIDTH_BY_LANGUAGE: { [languageCode: string]: number } = {
        'en-US': 1104,
        'es-MX': 1205
    };

    private _minDesktopWidth(): number {
        const languageCode = LocalizationService.normalizeLanguageCode(this._context.getProperty("languageCode") || "en-US");
        return Header.MIN_DESKTOP_WIDTH_BY_LANGUAGE[languageCode] ?? Header.MIN_DESKTOP_WIDTH_BY_LANGUAGE['en-US'];
    }

    private readonly _updateOrientation = (): void => {
        const ASPECT_RATIO = 1.2;
        const locationBar = document.getElementById("div_header_location_bar");

        if (window.innerWidth > window.innerHeight * ASPECT_RATIO && window.innerWidth >= this._minDesktopWidth() && !Util.isAppView()) {
            this._mobileElements().forEach(e => (e.style.display = "none"));
            this._desktopElements().forEach(e => (e.style.display = ""));

            if (locationBar)
                locationBar.style.display = "";

            Util.showElement("div_header_profile");

            this._isLandscape = true;
        }
        else {
            this._mobileElements().forEach(e => (e.style.display = ""));
            this._desktopElements().forEach(e => (e.style.display = "none"));

            if (locationBar)
                locationBar.style.display = "none";

            if (!this._isLoggedIn) Util.hideElement("div_header_profile");

            if (this._isLandscape)
                this._eventBus.emit(EventTypes.MODAL_CLOSE, Modals.HEADER_ACCOUNT);

            this._isLandscape = false;
        }

        this._updateCartDisplay();
        this._applyOrderHubMode();
    }

    /**
     * When running in Order Hub mode (context.isOrderHub = true):
     * - Desktop: hides all header content, leaving a blank header.
     * - Mobile: hides nav/login/back elements and shows only the order total area.
     */
    private readonly _applyOrderHubMode = (): void => {
        if (!this._context.getProperty("isOrderHub")) return;

        if (this._isLandscape) {
            // Desktop — blank header
            Util.hideElement("div_header_hamburger_menu");
            Util.hideElement("div_header_store_logo");
            Util.hideElement("div_header_right");
            Util.hideElement("div_header_location_bar");
            Util.hideElement("div_header_center_text");
            Util.hideElement("div_header_center_subtext");
            this._desktopElements().forEach(e => (e.style.display = "none"));
            this._mobileElements().forEach(e => (e.style.display = "none"));
        } else {
            // Mobile — blank header (match desktop)
            Util.hideElement("div_header_hamburger_menu");
            Util.hideElement("div_header_arrow_back");
            Util.showElement("div_header_center_text");
            Util.hideElement("div_header_login");
            Util.hideElement("div_header_right");
            Util.hideElement("div_header_shopping_bag");

            const center = document.querySelector<HTMLElement>(".header-center");
            if (center)
                center.style.alignItems = "center";
        }
    }

    private readonly _onBrandLoad = async (): Promise<void> => {
        this._setRedirectionUrls();
        await this._addBrandLinks();
    }

    private _setRedirectionUrls(): void {
        if (Util.isAppView())
            return;

        const logoAnchor = document.getElementById('a_header_logo') as HTMLAnchorElement;
        const homeLi = document.getElementById('li_header_home_nav');
        const homepageRedirectUrl = this._data.getBrandProperty("Settings").OOS.homepageRedirectUrl;

        if (homepageRedirectUrl) {
            if (homeLi)
                homeLi.innerHTML = `<a href="${homepageRedirectUrl}" ltag="Home">HOME</a>`;

            if (logoAnchor) {
                logoAnchor.href = homepageRedirectUrl;
                logoAnchor.onclick = null;
            }
        }

        const locationsLi = document.getElementById('li_header_locations_nav');
        const locationsRedirectUrl = this._data.getBrandProperty("Settings").OOS.storeLocatorRedirectUrl;
        if (locationsRedirectUrl && locationsLi)
            locationsLi.innerHTML = `<a href="${locationsRedirectUrl}" ltag="FindStore">FIND A STORE</a>`;
    }

    private async _addBrandLinks(): Promise<void> {
        const container = this._brandNavigationItems;
        if (!container) return;
        const url = `${this._context.getProperty("publicStorageUrl")}/online3/online-brand-header.html?v=${Date.now()}`;

        const res = await fetch(url);
        if (!res.ok) return;

        const html = await res.text();
        const tpl = document.createElement("template");
        tpl.innerHTML = html.trim();

        const anchors = Array.from(tpl.content.querySelectorAll<HTMLAnchorElement>("a"));
        if (anchors.length === 0) {
            container.innerHTML = html;
            return;
        }

        const parent = container.parentElement; // expect <ul>
        if (!parent) return;

        for (const a of anchors) {
            a.setAttribute("role", "link");
            const li = document.createElement("li");
            li.appendChild(a); // moves the <a> node from the template into the live DOM
            parent.insertBefore(li, container);
        }

        parent.removeChild(container);
    };

    /**
     * Handles updating the Header UI for the current order type with store info when the order type changes.
     */
    private readonly _orderTypeChanged = (): void => {
        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");
        let address: IAddressFormat | null = null;
        let storeKey: string = "";

        switch (orderTypeId) {
            case OrderType.DINE_IN:
            case OrderType.TAKE_OUT: {
                const store = this._data.getProperty("StoreInfo");
                address = {
                    StreetNo: store.streetNumber,
                    Address1: store.address1,
                    Address2: store.address2,
                    Address3: "",
                    Address4: "",
                    Address5: "",
                    City: "",
                    State: "",
                    ZipCode: "",
                    CountryID: store.countryId || 1,
                    AddressTypeID: 0
                };
                break;
            }
            case OrderType.DELIVERY: {
                const deliveryAddress = this._orderService.order.getProperty("Address");
                if (deliveryAddress === null) {
                    this._modules.orderLocationBar.updateAddress(null);
                    this._modules.orderLocationBar.storeKey = "";
                    return;
                }

                address = {
                    StreetNo: deliveryAddress.StreetNo,
                    Address1: deliveryAddress.StreetName,
                    Address2: deliveryAddress.Address2,
                    Address3: "",
                    Address4: "",
                    Address5: "",
                    City: "",
                    State: "",
                    ZipCode: "",
                    CountryID: deliveryAddress.CountryID || 1,
                    AddressTypeID: 0
                };
                storeKey = this._closestDeliveryStoreKey;
                break;
            }
        }

        this._modules.orderLocationBar.updateAddress(address);
        this._modules.orderLocationBar.storeKey = storeKey;
    }

    /**
     * Handles back navigation logic based on current and previous paths.
     * If on MENU_VIEW_ALL, it redirects back to MENU.
     * If the previous path requires a storeKey and it's missing, it skips 2 paths back.
     * Otherwise, pops a single path from history and navigates back.
     */
    private readonly _handleBackNavigation = (): void => {
        const currentPath = window.location.pathname as Pages;

        if (currentPath === Pages.MENU_VIEW_ALL) {
            this._previousPaths.pop();
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
            return;
        }

        const lastPath = this._previousPaths.at(-1) ?? Pages.LANDING;
        const pathsRequiringStore = [Pages.MENU, Pages.CART];

        const isMissingStore = pathsRequiringStore.includes(lastPath) && !this._data.storeKey;

        if (isMissingStore) {
            const secondLastPath = this._previousPaths.at(-2) ?? Pages.LANDING;
            const removeCount = Math.min(2, this._previousPaths.length);
            this._previousPaths.splice(-removeCount, removeCount);
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, secondLastPath);
            return;
        }

        this._previousPaths.pop();
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, lastPath);
    }
}